import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiClock, FiSearch, FiDownload, FiPrinter,
  FiDollarSign, FiAlertTriangle, FiChevronDown,
  FiChevronRight, FiUsers, FiTrendingUp, FiExternalLink
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { colors } from '../../theme/colors';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached } from '../../utils/masterDataStore';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { loadPartyLedgerBalances, loadAgingInvoices, allocateBalanceNewestFirst, openingItem, OPENING_ITEM_ID } from '../../utils/ledgerAgingHelper';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──

const AGING_PRESETS = {
  default: { label: 'Default (30-day)', buckets: [30, 60, 90, 120] },
  weekly: { label: 'Weekly (7-day)', buckets: [7, 14, 21, 28] },
  bimonthly: { label: 'Bi-Monthly (15-day)', buckets: [15, 30, 45, 60] },
  quarterly: { label: 'Quarterly (90-day)', buckets: [90, 180, 270, 360] },
};

const CHART_COLORS = [
  colors.success.main,    // Current / 0-30
  colors.warning.main,    // 31-60
  '#FF8C00',              // 61-90 (dark orange)
  colors.error.main,      // 91-120
  '#8B0000',              // 120+ (dark red)
];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const daysBetween = (dateStr1, dateStr2) => {
  const d1 = new Date(dateStr1);
  const d2 = new Date(dateStr2);
  const diff = d2.getTime() - d1.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const ARAging = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('ar-aging');
  const { formatCurrency } = useCurrency();
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';

  // ── Data state ──
  const [invoices, setInvoices] = useState(() => savedMem?.invoices ?? []);
  const [receipts, setReceipts] = useState(() => savedMem?.receipts ?? []);
  const [customers, setCustomers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [regions, setRegions] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);

  // ── Filter state ──
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [asOfDate, setAsOfDate] = useState(() => savedMem?.asOfDate ?? new Date().toISOString().split('T')[0]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => savedMem?.selectedCustomerId ?? '');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? '');
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? '');
  // Salesman filter — matches against the customer's saved default salesperson
  // (set on the Customer master). Empty = "all salesmen".
  const [selectedSalesPersonId, setSelectedSalesPersonId] = useState(() => savedMem?.selectedSalesPersonId ?? '');
  const [employees, setEmployees] = useState([]);
  const [agingPreset, setAgingPreset] = useState(() => savedMem?.agingPreset ?? 'default');
  const [minBalance, setMinBalance] = useState(() => savedMem?.minBalance ?? 0);
  const [taxFilter, setTaxFilter] = useState(() => savedMem?.taxFilter ?? 'both');
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');

  // ── Detail expansion ──
  const [expandedCustomerId, setExpandedCustomerId] = useState(() => savedMem?.expandedCustomerId ?? null);

  // ── Load financial years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // ── Load master data ──
  const loadMasterData = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [custSnap, areaSnap, regionSnap, empRows] = await Promise.all([
        getDocs(query(collection(db, 'companies', activeCompany.id, 'customers'), orderBy('companyName', 'asc'))),
        getDocs(collection(db, 'companies', activeCompany.id, 'areas')),
        getDocs(collection(db, 'companies', activeCompany.id, 'regions')),
        getEmployeesCached(activeCompany.id),
      ]);
      recordReads(activeCompany.id, custSnap.size + areaSnap.size + regionSnap.size);

      setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAreas(areaSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setRegions(regionSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setEmployees(
        empRows.filter(e => e.isActive !== false && e.isDeleted !== true)
      );
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadMasterData();
  }, [loadFinancialYears, loadMasterData]);

  // ── Generate Report ──
  const handleGenerateReport = async () => {
    if (!activeCompany?.id) return;

    setLoading(true);
    setHasGenerated(true);
    setExpandedCustomerId(null);

    try {
      // ACC-10 fix (2026-09-30): outstanding = the customer's LEDGER balance as of
      // the date (same figure as Customer Balances / Ledger), spread over the
      // customer's invoices newest-first. Returns, receipts, JVs, opening
      // balances, WHT and write-offs are all reflected automatically because
      // they all post to the customer's ledger account. See ledgerAgingHelper.js.
      const branchId = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;
      const [{ balances }, allInvoices] = await Promise.all([
        loadPartyLedgerBalances(activeCompany.id, { entityType: 'customer', fyId: selectedFYId, asOfDate, branchId }),
        loadAgingInvoices(activeCompany.id, { codes: ['SI', 'NSI', 'PS'], fyId: selectedFYId, asOfDate, branchId }),
      ]);
      const taxFilterFn = t => {
        if (taxFilter === 'tax') return t.tranTypeCode !== 'NSI';
        if (taxFilter === 'nonTax') return t.tranTypeCode !== 'SI';
        return true;
      };
      const fy = financialYears.find(f => f.id === selectedFYId);
      const openingDate = (fy && fy.startDate) || asOfDate;

      const invByCustomer = {};
      allInvoices.forEach(inv => {
        (invByCustomer[inv.partyId] = invByCustomer[inv.partyId] || []).push(inv);
      });
      const nextInvoices = [];
      Object.keys(balances).forEach(custId => {
        const bal = balances[custId];
        if (!(bal > 0.009)) return; // settled, or customer in credit (advance)
        const { items, openingRemainder } = allocateBalanceNewestFirst(bal, invByCustomer[custId] || []);
        if (openingRemainder > 0) nextInvoices.push({ ...openingItem(openingRemainder, openingDate), partyId: custId });
        items.filter(taxFilterFn).forEach(i => nextInvoices.push(i));
      });
      const nextReceipts = [];
      setInvoices(nextInvoices);
      setReceipts(nextReceipts);

      // Remember this generated view so Back restores it with 0 re-reads.
      saveMemory({
        invoices: nextInvoices, receipts: nextReceipts, hasGenerated: true,
        expandedCustomerId: null,
        selectedFYId, asOfDate, selectedCustomerId, selectedAreaId, selectedRegionId,
        selectedSalesPersonId, agingPreset, minBalance, taxFilter, searchQuery
      });
    } catch (error) {
      console.error('Error generating AR aging report:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Build customer map ──
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // ── Get current aging buckets from preset ──
  const agingBuckets = useMemo(() => {
    const preset = AGING_PRESETS[agingPreset] || AGING_PRESETS.default;
    return preset.buckets;
  }, [agingPreset]);

  // ── Build bucket labels ──
  const bucketLabels = useMemo(() => {
    const labels = [];
    const b = agingBuckets;
    labels.push(`0-${b[0]}`);
    for (let i = 1; i < b.length; i++) {
      labels.push(`${b[i - 1] + 1}-${b[i]}`);
    }
    labels.push(`${b[b.length - 1]}+`);
    return labels;
  }, [agingBuckets]);

  // ── Compute aging data per customer ──
  const agingData = useMemo(() => {
    if (!hasGenerated) return [];

    // Aggregate total receipts per customer
    const receiptsByCustomer = {};
    receipts.forEach(r => {
      const custId = r.partyId;
      if (!custId) return;
      if (!receiptsByCustomer[custId]) receiptsByCustomer[custId] = 0;
      // Bank/Cash receipts with WHT / bad-debt deductions clear MORE than the cash
      // received: the customer's dues drop by cash + WHT + bad debt. (Derived —
      // `grossAmount` is a subtotal alias, always 0 on receipts.) Old receipts have
      // no deductions, so this equals their totalAmount; other docs are unchanged.
      const isRcpt = r.tranTypeCode === 'CR' || r.tranTypeCode === 'BR';
      receiptsByCustomer[custId] += isRcpt
        ? (Number(r.totalAmount) || 0) + (Number(r.whtAmount) || 0) + (Number(r.adjustmentAmount) || 0)
        : (r.netAmount || r.totalAmount || 0);
    });

    // Group invoices by customer
    const invoicesByCustomer = {};
    invoices.forEach(inv => {
      const custId = inv.partyId;
      if (!custId) return;
      if (!invoicesByCustomer[custId]) invoicesByCustomer[custId] = [];
      invoicesByCustomer[custId].push(inv);
    });

    // For each customer, compute aging
    const results = [];
    const allCustomerIds = new Set([
      ...Object.keys(invoicesByCustomer),
      ...Object.keys(receiptsByCustomer),
    ]);

    allCustomerIds.forEach(custId => {
      const custInvoices = invoicesByCustomer[custId] || [];
      const customerInfo = customerMap[custId] || {};

      // Sort invoices by date (oldest first) for FIFO allocation
      const sortedInvoices = [...custInvoices].sort((a, b) =>
        (a.docDate || '').localeCompare(b.docDate || '')
      );

      // Calculate total invoice amount
      const totalInvoiced = sortedInvoices.reduce((sum, inv) =>
        sum + (inv.invoiceAmount ?? (inv.netAmount || inv.totalAmount || 0)), 0
      );

      // Outstanding already comes from the ledger allocation (ACC-10).
      const processedInvoices = sortedInvoices.map(inv => ({
        ...inv,
        invoiceAmount: inv.invoiceAmount ?? (inv.netAmount || inv.totalAmount || 0),
        outstanding: Number(inv.outstanding) || 0,
        daysOutstanding: daysBetween(inv.docDate || asOfDate, asOfDate),
      }));

      // Place outstanding amounts into aging buckets
      const numBuckets = agingBuckets.length + 1;
      const bucketAmounts = new Array(numBuckets).fill(0);

      processedInvoices.forEach(inv => {
        if (inv.outstanding <= 0) return;

        const days = inv.daysOutstanding;
        let bucketIdx = numBuckets - 1; // default: last bucket (overflow)

        for (let i = 0; i < agingBuckets.length; i++) {
          if (i === 0 && days <= agingBuckets[0]) {
            bucketIdx = 0;
            break;
          } else if (i > 0 && days > agingBuckets[i - 1] && days <= agingBuckets[i]) {
            bucketIdx = i;
            break;
          }
        }

        bucketAmounts[bucketIdx] += inv.outstanding;
      });

      const totalOutstanding = bucketAmounts.reduce((a, b) => a + b, 0);
      // Received / adjusted against these invoices = invoiced − still owed
      const totalReceiptsLedger = Math.max(0, Math.round((totalInvoiced - totalOutstanding) * 100) / 100);

      results.push({
        customerId: custId,
        customerCode: customerInfo.customerCode || customerInfo.companyName?.substring(0, 6)?.toUpperCase() || '-',
        customerName: customerInfo.companyName || custInvoices[0]?.partyName || 'Unknown Customer',
        areaId: customerInfo.areaId || '',
        regionId: customerInfo.regionId || '',
        // Salesperson tag carried from the customer master so the table can
        // both filter by salesperson and (optionally later) show their name.
        salesPersonId: customerInfo.salesPersonId || '',
        salesPersonName: customerInfo.salesPersonName || '',
        creditLimit: customerInfo.creditLimit || 0,
        creditDays: customerInfo.creditDays || 0,
        bucketAmounts,
        totalOutstanding,
        totalInvoiced,
        totalReceipts: totalReceiptsLedger,
        invoiceCount: custInvoices.filter(i => !i.isOpeningLine).length,
        invoices: processedInvoices,
      });
    });

    return results;
  }, [hasGenerated, invoices, receipts, customerMap, agingBuckets, asOfDate]);

  // ── Apply filters to aging data ──
  const filteredData = useMemo(() => {
    let data = agingData;

    // Customer filter
    if (selectedCustomerId) {
      data = data.filter(d => d.customerId === selectedCustomerId);
    }

    // Area filter
    if (selectedAreaId) {
      data = data.filter(d => d.areaId === selectedAreaId);
    }

    // Region filter
    if (selectedRegionId) {
      data = data.filter(d => d.regionId === selectedRegionId);
    }

    // Salesman filter — by the customer's assigned default salesperson.
    if (selectedSalesPersonId) {
      data = data.filter(d => d.salesPersonId === selectedSalesPersonId);
    }

    // Minimum balance filter
    if (minBalance > 0) {
      data = data.filter(d => d.totalOutstanding >= minBalance);
    }

    // Only show customers with outstanding > 0
    data = data.filter(d => d.totalOutstanding > 0.01);

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(d =>
        (d.customerCode || '').toLowerCase().includes(q) ||
        (d.customerName || '').toLowerCase().includes(q)
      );
    }

    // Sort by total outstanding descending
    data.sort((a, b) => b.totalOutstanding - a.totalOutstanding);

    return data;
  }, [agingData, selectedCustomerId, selectedAreaId, selectedRegionId, selectedSalesPersonId, minBalance, searchQuery]);

  // ── Summary calculations ──
  const summary = useMemo(() => {
    const numBuckets = agingBuckets.length + 1;
    const bucketTotals = new Array(numBuckets).fill(0);

    filteredData.forEach(row => {
      row.bucketAmounts.forEach((amt, i) => {
        bucketTotals[i] += amt;
      });
    });

    const totalReceivable = bucketTotals.reduce((a, b) => a + b, 0);

    return {
      totalReceivable,
      bucketTotals,
      customerCount: filteredData.length,
      current: bucketTotals[0] || 0,
      overdue31_60: bucketTotals[1] || 0,
      overdue61_90: bucketTotals[2] || 0,
      overdue90Plus: bucketTotals.slice(3).reduce((a, b) => a + b, 0),
    };
  }, [filteredData, agingBuckets]);

  // ── Chart: Top 10 Stacked Bar (Aging by Customer) ──
  const stackedBarData = useMemo(() => {
    const top10 = filteredData.slice(0, 10);
    return top10.map(row => {
      const entry = {
        name: row.customerName.length > 18
          ? row.customerName.substring(0, 18) + '...'
          : row.customerName,
        fullName: row.customerName,
      };
      bucketLabels.forEach((label, i) => {
        entry[label] = row.bucketAmounts[i] || 0;
      });
      return entry;
    });
  }, [filteredData, bucketLabels]);

  // ── Chart: Pie (by aging bucket) ──
  const pieData = useMemo(() => {
    return bucketLabels.map((label, i) => ({
      name: label + ' Days',
      value: summary.bucketTotals[i] || 0,
    })).filter(d => d.value > 0);
  }, [bucketLabels, summary.bucketTotals]);

  // ── Chart: Top 10 Customers by Outstanding ──
  const top10BarData = useMemo(() => {
    return filteredData.slice(0, 10).map(row => ({
      name: row.customerName.length > 18
        ? row.customerName.substring(0, 18) + '...'
        : row.customerName,
      fullName: row.customerName,
      outstanding: row.totalOutstanding,
    }));
  }, [filteredData]);

  // ── Toggle expanded customer row ──
  const handleRowClick = (custId) => {
    setExpandedCustomerId(prev => prev === custId ? null : custId);
  };

  // ── CSV Export ──
  const handleExport = () => {
    if (filteredData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Accounts Receivable Aging Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `As Of Date: ${formatDateStr(asOfDate)}\n`;
    csv += `Generated: ${formatDateStr(new Date().toISOString().split('T')[0])}\n\n`;

    // Header row
    csv += 'Customer Code,Customer Name';
    bucketLabels.forEach(label => {
      csv += `,${label} Days`;
    });
    csv += ',Total Outstanding,% of Total\n';

    // Data rows
    filteredData.forEach(row => {
      const pct = summary.totalReceivable > 0
        ? ((row.totalOutstanding / summary.totalReceivable) * 100).toFixed(1)
        : '0.0';
      csv += `"${row.customerCode}","${(row.customerName || '').replace(/"/g, '""')}"`;
      row.bucketAmounts.forEach(amt => {
        csv += `,${amt.toFixed(2)}`;
      });
      csv += `,${row.totalOutstanding.toFixed(2)},${pct}%\n`;
    });

    // Totals row
    csv += '\n,Totals';
    summary.bucketTotals.forEach(total => {
      csv += `,${total.toFixed(2)}`;
    });
    csv += `,${summary.totalReceivable.toFixed(2)},100%\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AR_Aging_${asOfDate || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>AR Aging Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:22px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary{display:flex;gap:20px;margin-bottom:20px;padding:15px;background:#f9f9f9;border-radius:8px;flex-wrap:wrap}
        .summary-item{text-align:center;min-width:120px}
        .summary-label{font-size:11px;color:#666;margin-bottom:4px}
        .summary-value{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px;font-size:11px}
        th{background:#800020;color:white;padding:8px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.amount{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:11px}
        td.amount{text-align:right;font-family:'Consolas',monospace}
        .total-row{background:#800020;color:white;font-weight:700}
        .total-row td{border-bottom:none;padding:10px}
        .current{color:#28A745}
        .warning{color:#FFC107}
        .orange{color:#FF8C00}
        .danger{color:#DC3545}
        .critical{color:#8B0000}
        @media print{body{margin:15px}table{page-break-inside:auto}}
      </style></head><body>
      <h1>Accounts Receivable Aging Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        As Of Date: ${formatDateStr(asOfDate)} |
        Generated: ${formatDateStr(new Date().toISOString().split('T')[0])}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Total Receivable</div>
          <div class="summary-value">${formatAmount(summary.totalReceivable)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Current (${bucketLabels[0]})</div>
          <div class="summary-value current">${formatAmount(summary.current)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Overdue ${bucketLabels[1] || '31-60'}</div>
          <div class="summary-value warning">${formatAmount(summary.overdue31_60)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Overdue ${bucketLabels[2] || '61-90'}</div>
          <div class="summary-value orange">${formatAmount(summary.overdue61_90)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Overdue 90+</div>
          <div class="summary-value danger">${formatAmount(summary.overdue90Plus)}</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>Code</th>
          <th>Customer Name</th>
          ${bucketLabels.map((l, i) => `<th class="amount">${l}</th>`).join('')}
          <th class="amount">Total</th>
          <th class="amount">%</th>
        </tr></thead>
        <tbody>
          ${filteredData.map(row => {
            const pct = summary.totalReceivable > 0
              ? ((row.totalOutstanding / summary.totalReceivable) * 100).toFixed(1)
              : '0.0';
            const colorClasses = ['current', 'warning', 'orange', 'danger', 'critical'];
            return `<tr>
              <td>${row.customerCode}</td>
              <td>${row.customerName}</td>
              ${row.bucketAmounts.map((amt, i) =>
                `<td class="amount ${colorClasses[i] || 'critical'}">${amt > 0 ? formatAmount(amt) : '-'}</td>`
              ).join('')}
              <td class="amount" style="font-weight:600">${formatAmount(row.totalOutstanding)}</td>
              <td class="amount">${pct}%</td>
            </tr>`;
          }).join('')}
          <tr class="total-row">
            <td colspan="2" style="text-align:right;font-weight:700">Grand Total</td>
            ${summary.bucketTotals.map(t => `<td class="amount">${formatAmount(t)}</td>`).join('')}
            <td class="amount">${formatAmount(summary.totalReceivable)}</td>
            <td class="amount">100%</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // ── Custom Tooltips ──
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const entry = payload[0]?.payload;
      return (
        <TooltipBox>
          <TooltipTitle>{entry?.fullName || label}</TooltipTitle>
          {payload.map((p, i) => (
            <TooltipRow key={i}>
              <TooltipDot style={{ background: p.color }} />
              <span>{p.name}: {currencySymbol}{formatAmount(p.value)}</span>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const pct = summary.totalReceivable > 0
        ? ((data.value / summary.totalReceivable) * 100).toFixed(1)
        : '0.0';
      return (
        <TooltipBox>
          <TooltipTitle>{data.name}</TooltipTitle>
          <TooltipRow>
            <span>Amount: {currencySymbol}{formatAmount(data.value)}</span>
          </TooltipRow>
          <TooltipRow>
            <span>Share: {pct}%</span>
          </TooltipRow>
        </TooltipBox>
      );
    }
    return null;
  };

  const CustomTop10Tooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const entry = payload[0]?.payload;
      return (
        <TooltipBox>
          <TooltipTitle>{entry?.fullName || label}</TooltipTitle>
          <TooltipRow>
            <span>Outstanding: {currencySymbol}{formatAmount(payload[0].value)}</span>
          </TooltipRow>
        </TooltipBox>
      );
    }
    return null;
  };

  // ── Get bucket color by index ──
  const getBucketColor = (index) => CHART_COLORS[index] || CHART_COLORS[CHART_COLORS.length - 1];

  return (
    <PageLayout pageTitle="AR Aging" breadcrumb="Reports / Sale Reports / AR Aging">
      {/* ═══════ Filters ═══════ */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>As Of Date</Label>
            <Input
              type="date"
              value={asOfDate}
              onChange={e => setAsOfDate(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              onChange={setSelectedCustomerId}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>
          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="">All Areas</option>
              {areas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName || a.name}</option>
              ))}
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => setSelectedRegionId(e.target.value)}>
              <option value="">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName || r.name}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Salesman</Label>
            <Select value={selectedSalesPersonId} onChange={e => setSelectedSalesPersonId(e.target.value)}>
              <option value="">All Salesmen</option>
              {employees.map(emp => {
                const label = `${emp.employeeCode ? emp.employeeCode + ' - ' : ''}${emp.firstName || ''} ${emp.lastName || ''}`.trim()
                  || emp.employeeName
                  || '(unnamed)';
                return <option key={emp.id} value={emp.id}>{label}</option>;
              })}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Aging Buckets</Label>
            <Select value={agingPreset} onChange={e => setAgingPreset(e.target.value)}>
              {Object.entries(AGING_PRESETS).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Min Balance</Label>
            <Input
              type="number"
              min="0"
              value={minBalance}
              onChange={e => setMinBalance(Number(e.target.value) || 0)}
              placeholder="0"
              style={{ width: 120 }}
            />
          </FilterGroup>
          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
          <SearchWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search customer code or name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchWrap>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={filteredData.length === 0}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint} disabled={filteredData.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
              <FiClock /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* ═══════ Summary Cards ═══════ */}
      {hasGenerated && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon color={colors.primary.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Receivable</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalReceivable)}</SummaryValue>
              <SummaryMeta>{formatNumber(summary.customerCount)} customers</SummaryMeta>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color={colors.success.main}><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Current ({bucketLabels[0]} Days)</SummaryLabel>
              <SummaryValue style={{ color: colors.success.main }}>
                {formatCurrency(summary.current)}
              </SummaryValue>
              <SummaryMeta>
                {summary.totalReceivable > 0
                  ? ((summary.current / summary.totalReceivable) * 100).toFixed(1)
                  : '0.0'}% of total
              </SummaryMeta>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color={colors.warning.main}><FiClock /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Overdue {bucketLabels[1] || '31-60'} Days</SummaryLabel>
              <SummaryValue style={{ color: colors.warning.main }}>
                {formatCurrency(summary.overdue31_60)}
              </SummaryValue>
              <SummaryMeta>
                {summary.totalReceivable > 0
                  ? ((summary.overdue31_60 / summary.totalReceivable) * 100).toFixed(1)
                  : '0.0'}% of total
              </SummaryMeta>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#FF8C00"><FiAlertTriangle /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Overdue {bucketLabels[2] || '61-90'} Days</SummaryLabel>
              <SummaryValue style={{ color: '#FF8C00' }}>
                {formatCurrency(summary.overdue61_90)}
              </SummaryValue>
              <SummaryMeta>
                {summary.totalReceivable > 0
                  ? ((summary.overdue61_90 / summary.totalReceivable) * 100).toFixed(1)
                  : '0.0'}% of total
              </SummaryMeta>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard $critical>
            <SummaryIcon color={colors.error.main}><FiAlertTriangle /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Overdue 90+ Days</SummaryLabel>
              <SummaryValue style={{ color: colors.error.main }}>
                {formatCurrency(summary.overdue90Plus)}
              </SummaryValue>
              <SummaryMeta>
                {summary.totalReceivable > 0
                  ? ((summary.overdue90Plus / summary.totalReceivable) * 100).toFixed(1)
                  : '0.0'}% of total
              </SummaryMeta>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* ═══════ Data Table ═══════ */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiClock /></EmptyIcon>
              <EmptyText>Generating AR aging report...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiClock /></EmptyIcon>
              <EmptyText>Select a financial year and click Generate to view the AR aging report</EmptyText>
            </EmptyState>
          ) : filteredData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiUsers /></EmptyIcon>
              <EmptyText>No outstanding receivables found for the selected criteria</EmptyText>
              <p style={{ fontSize: '14px', color: colors.text.tertiary, marginTop: 8 }}>
                Try adjusting your filters or lowering the minimum balance threshold
              </p>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 30 }}></Th>
                  <Th style={{ width: 100 }}>Code</Th>
                  <Th>Customer Name</Th>
                  {bucketLabels.map((label, i) => (
                    <Th key={label} align="right" style={{ minWidth: 100 }}>
                      <BucketHeader $color={getBucketColor(i)}>
                        {label} Days
                      </BucketHeader>
                    </Th>
                  ))}
                  <Th align="right" style={{ minWidth: 120 }}>Total</Th>
                  <Th align="right" style={{ width: 70 }}>%</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredData.map((row) => {
                  const pct = summary.totalReceivable > 0
                    ? ((row.totalOutstanding / summary.totalReceivable) * 100).toFixed(1)
                    : '0.0';
                  const isExpanded = expandedCustomerId === row.customerId;

                  return (
                    <React.Fragment key={row.customerId}>
                      <Tr
                        $clickable
                        onClick={() => handleRowClick(row.customerId)}
                        $isExpanded={isExpanded}
                      >
                        <Td style={{ textAlign: 'center', padding: '8px 4px', width: 30 }}>
                          {isExpanded
                            ? <FiChevronDown size={14} style={{ color: colors.text.secondary }} />
                            : <FiChevronRight size={14} style={{ color: colors.text.secondary }} />
                          }
                        </Td>
                        <Td>
                          <CustomerCode>{row.customerCode}</CustomerCode>
                        </Td>
                        <Td>
                          <CustomerName>
                            {row.customerName}
                            {row.creditLimit > 0 && (
                              <CreditBadge
                                $overLimit={row.totalOutstanding > row.creditLimit}
                              >
                                Limit: {formatCurrency(row.creditLimit, { compact: true })}
                              </CreditBadge>
                            )}
                          </CustomerName>
                        </Td>
                        {row.bucketAmounts.map((amt, i) => (
                          <AmountCell key={i} align="right" $bucketColor={getBucketColor(i)}>
                            {amt > 0.01 ? formatAmount(amt) : '-'}
                          </AmountCell>
                        ))}
                        <AmountCell align="right" $isTotal>
                          {formatAmount(row.totalOutstanding)}
                        </AmountCell>
                        <AmountCell align="right">
                          {pct}%
                        </AmountCell>
                      </Tr>

                      {/* ── Expanded Invoice Detail ── */}
                      {isExpanded && (
                        <ExpandedRow>
                          <Td colSpan={bucketLabels.length + 5} style={{ padding: 0 }}>
                            <DetailPanel>
                              <DetailHeader>
                                <DetailTitle>Invoice Details - {row.customerName}</DetailTitle>
                                <DetailSummary>
                                  <span>Invoices: {row.invoiceCount}</span>
                                  <span>Total Invoiced: {formatCurrency(row.totalInvoiced)}</span>
                                  <span>Receipts: {formatCurrency(row.totalReceipts)}</span>
                                  <span>Outstanding: {formatCurrency(row.totalOutstanding)}</span>
                                  <ActionLinkBtn
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/receipts/cash-receipt', {
                                        state: {
                                          prefill: {
                                            partyId: row.customerId,
                                            partyName: row.customerName,
                                            linkedAccountId: row.linkedAccountId || null
                                          }
                                        }
                                      });
                                    }}
                                  >
                                    <FiDollarSign size={12} /> Receive Payment
                                  </ActionLinkBtn>
                                </DetailSummary>
                              </DetailHeader>
                              <DetailTable>
                                <thead>
                                  <DetailTr>
                                    <DetailTh>Doc #</DetailTh>
                                    <DetailTh>Date</DetailTh>
                                    <DetailTh align="right">Invoice Amt</DetailTh>
                                    <DetailTh align="right">Outstanding</DetailTh>
                                    <DetailTh align="right">Days</DetailTh>
                                    <DetailTh>Aging Bucket</DetailTh>
                                    <DetailTh>Status</DetailTh>
                                  </DetailTr>
                                </thead>
                                <tbody>
                                  {row.invoices
                                    .filter(inv => inv.outstanding > 0.01)
                                    .sort((a, b) => a.daysOutstanding - b.daysOutstanding)
                                    .map((inv, idx) => {
                                      // Determine bucket
                                      let bucketLabel = bucketLabels[bucketLabels.length - 1] + '+';
                                      let bucketColor = getBucketColor(bucketLabels.length - 1);
                                      for (let i = 0; i < agingBuckets.length; i++) {
                                        if (i === 0 && inv.daysOutstanding <= agingBuckets[0]) {
                                          bucketLabel = bucketLabels[0];
                                          bucketColor = getBucketColor(0);
                                          break;
                                        } else if (i > 0 && inv.daysOutstanding > agingBuckets[i - 1] && inv.daysOutstanding <= agingBuckets[i]) {
                                          bucketLabel = bucketLabels[i];
                                          bucketColor = getBucketColor(i);
                                          break;
                                        }
                                      }

                                      return (
                                        <DetailTr key={inv.id || idx}>
                                          <DetailTd>
                                            <span
                                              style={{ cursor: 'pointer', color: '#1a73e8' }}
                                              onClick={(e) => { e.stopPropagation(); if (inv.id === OPENING_ITEM_ID) return; navigateToTransaction(navigate, inv.tranTypeCode || 'SI', inv.id); }}
                                              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                            >
                                              {inv.docNumber || '-'} <FiExternalLink size={11} style={{ marginLeft: 3, verticalAlign: 'middle' }} />
                                            </span>
                                          </DetailTd>
                                          <DetailTd>{formatDateStr(inv.docDate)}</DetailTd>
                                          <DetailTd align="right">{formatAmount(inv.invoiceAmount)}</DetailTd>
                                          <DetailTd align="right" style={{ fontWeight: 600 }}>
                                            {formatAmount(inv.outstanding)}
                                          </DetailTd>
                                          <DetailTd align="right">{inv.daysOutstanding}</DetailTd>
                                          <DetailTd>
                                            <AgingBadge $color={bucketColor}>{bucketLabel} Days</AgingBadge>
                                          </DetailTd>
                                          <DetailTd>
                                            <StatusBadge $status={inv.status}>{inv.status}</StatusBadge>
                                          </DetailTd>
                                        </DetailTr>
                                      );
                                    })}
                                  {row.invoices.filter(inv => inv.outstanding > 0.01).length === 0 && (
                                    <DetailTr>
                                      <DetailTd colSpan={7} style={{ textAlign: 'center', color: colors.text.tertiary }}>
                                        All invoices have been fully paid
                                      </DetailTd>
                                    </DetailTr>
                                  )}
                                </tbody>
                              </DetailTable>
                            </DetailPanel>
                          </Td>
                        </ExpandedRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={3} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}>
                    Grand Total ({filteredData.length} customers)
                  </Td>
                  {summary.bucketTotals.map((total, i) => (
                    <AmountCell key={i} align="right" $isTotal $bucketColor={getBucketColor(i)}>
                      {formatAmount(total)}
                    </AmountCell>
                  ))}
                  <AmountCell align="right" $isTotal $isGrand>
                    {formatAmount(summary.totalReceivable)}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    100%
                  </AmountCell>
                </TotalRow>
              </Tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* ═══════ Charts ═══════ */}
      {hasGenerated && !loading && filteredData.length > 0 && (
        <>
          <ChartsRow>
            {/* Stacked Bar: Aging by Customer (Top 10) */}
            <ChartCard>
              <ChartTitle>Aging Distribution by Customer (Top 10)</ChartTitle>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={stackedBarData} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis
                    dataKey="name"
                    fontSize={10}
                    tick={{ fill: colors.text.secondary }}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    fontSize={11}
                    tick={{ fill: colors.text.secondary }}
                    tickFormatter={(v) => {
                      if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
                      if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
                      if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                      return v;
                    }}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {bucketLabels.map((label, i) => (
                    <Bar
                      key={label}
                      dataKey={label}
                      name={label + ' Days'}
                      stackId="aging"
                      fill={getBucketColor(i)}
                      radius={i === bucketLabels.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Pie: Total by Aging Bucket */}
            <ChartCard>
              <ChartTitle>Receivable by Aging Bucket</ChartTitle>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={105}
                    innerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true}
                    fontSize={10}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getBucketColor(index)} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsRow>

          {/* Bar: Top 10 by Outstanding */}
          <ChartsRowSingle>
            <ChartCard>
              <ChartTitle>Top 10 Customers by Outstanding Amount</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top10BarData} margin={{ top: 10, right: 30, left: 10, bottom: 40 }} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis
                    type="number"
                    fontSize={11}
                    tick={{ fill: colors.text.secondary }}
                    tickFormatter={(v) => {
                      if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
                      if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
                      if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                      return v;
                    }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    fontSize={11}
                    tick={{ fill: colors.text.secondary }}
                    width={140}
                  />
                  <Tooltip content={<CustomTop10Tooltip />} />
                  <Bar dataKey="outstanding" name="Outstanding" fill={colors.primary.main} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsRowSingle>
        </>
      )}
    </PageLayout>
  );
};

// ════════════════════════════════════════════
// ═══════ STYLED COMPONENTS ════════════════
// ════════════════════════════════════════════

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    border-color: ${colors.primary.main};
    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover:not(:disabled) {
      background: ${colors.neutral[100]};
      border-color: ${colors.primary.main};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ── Summary Cards ──

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  ${props => props.$critical && `
    border-left: 4px solid ${colors.error.main};
  `}
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummaryMeta = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

// ── Charts ──

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartsRowSingle = styled.div`
  margin-bottom: 20px;
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 12px;
  box-shadow: ${colors.shadows.md};
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-top: 4px;
`;

const TooltipDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const BucketHeader = styled.span`
  color: ${props => props.$color || colors.text.secondary};
  font-weight: 700;
`;

const Tbody = styled.tbody``;

const Tfoot = styled.tfoot``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  ${props => props.$clickable && `
    cursor: pointer;
    &:hover { background: ${colors.neutral[50]}; }
  `}

  ${props => props.$isExpanded && `
    background: ${colors.primary.main}06;
    border-bottom-color: ${colors.primary.main}20;
  `}
`;

const ExpandedRow = styled.tr`
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;

  ${props => props.$bucketColor && `
    color: ${props.$bucketColor};
  `}

  ${props => props.$isTotal && `
    font-weight: 700;
    font-size: 13px;
  `}

  ${props => props.$isGrand && `
    font-size: 14px;
    color: ${colors.text.primary};
  `}
`;

const CustomerCode = styled.span`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${colors.neutral[100]};
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

const CustomerName = styled.div`
  font-size: 13px;
  color: ${colors.text.primary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CreditBadge = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${props => props.$overLimit ? colors.error.main + '15' : colors.info.main + '15'};
  color: ${props => props.$overLimit ? colors.error.main : colors.info.main};
  white-space: nowrap;
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }
`;

// ── Detail Panel (expanded row) ──

const DetailPanel = styled.div`
  padding: 16px 20px;
  background: ${colors.neutral[50]};
  border-top: 1px solid ${colors.neutral[200]};
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const DetailTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.primary.main};
  margin: 0;
`;

const DetailSummary = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;

  span {
    font-size: 12px;
    color: ${colors.text.secondary};
    font-weight: 500;
  }
`;

const ActionLinkBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px;
  background: ${colors.success.main};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { background: ${colors.success.dark || colors.success.main}; opacity: 0.9; }
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: ${colors.borderRadius.md};
  overflow: hidden;
  box-shadow: ${colors.shadows.sm};
`;

const DetailTr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailTh = styled.th`
  padding: 10px 12px;
  text-align: ${props => props.align || 'left'};
  font-size: 10px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const DetailTd = styled.td`
  padding: 8px 12px;
  font-size: 12px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  font-family: ${props => props.align === 'right' ? "'SF Mono','Consolas','Courier New',monospace" : 'inherit'};
`;

const AgingBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${props => props.$color || colors.neutral[300]}15;
  color: ${props => props.$color || colors.text.secondary};
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;

  ${props => {
    switch (props.$status) {
      case 'Posted':
        return `background: ${colors.info.main}15; color: ${colors.info.main};`;
      case 'Partial':
        return `background: ${colors.warning.main}15; color: ${colors.warning.main};`;
      case 'Paid':
        return `background: ${colors.success.main}15; color: ${colors.success.main};`;
      default:
        return `background: ${colors.neutral[200]}; color: ${colors.text.secondary};`;
    }
  }}
`;

// ── Empty State ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default ARAging;
